/**
 * Bounded batch execution for the model-facing `id_lookup` tool.
 *
 * FactSet accepts one `searchText` per `/idsearch/v2/lookup` request. This
 * module gives the agent one tool call that fans those requests out through the
 * existing single-company route, with a fixed candidate limit, shared rate-limit
 * backoff, and an isolated result for every submitted entry.
 */

import {
  FACTSET_ID_LOOKUP_CLIENT_TIMEOUT_MS,
  FACTSET_ID_LOOKUP_DEFAULT_LIMIT,
  FACTSET_ID_LOOKUP_ERROR_CODES,
  FACTSET_ID_LOOKUP_MAX_RETRY_DELAY_MS,
} from "../shared/factset/factsetConstants";
import {
  describeIdLookupSecurityTypes,
  ID_LOOKUP_LISTING_REGION_NAMES,
  ID_LOOKUP_SECURITY_TYPE_NAMES,
  listingRegionWireValue,
  securityTypeWireValue,
} from "./idLookupCatalog";
import {
  IdLookupFailedError,
  IdLookupReauthRequiredError,
  IdLookupTimeoutError,
  type IdLookupFilters,
  type IdLookupRequest,
  type IdLookupResponse,
} from "./services/factset/idLookupApi";

/** FactSet requests allowed to run at the same time for one lookup call. */
export const ID_LOOKUP_BATCH_CONCURRENCY = 20;

/** Wall-clock budget for one model-facing lookup call. */
export const ID_LOOKUP_BATCH_TIMEOUT_MS = 5 * 60 * 1000;

const ID_LOOKUP_INPUT_ERROR_NAME = "IdLookupInputError";
const TIMEOUT_MESSAGE = "FactSet company lookup timed out.";
const RATE_LIMIT_MESSAGE = "FactSet company lookup is rate limited.";

/**
 * One validated company lookup after semantic filters have been translated.
 */
export interface ParsedIdLookupEntry {
  /** Opaque correlation key supplied by the agent, usually a worksheet row. */
  readonly requestKey: string;
  /** Trimmed company name or identifier sent to FactSet. */
  readonly searchText: string;
  /** Existing single-company route payload, including the fixed candidate limit. */
  readonly request: IdLookupRequest;
}

/**
 * Upstream lookup used by the batch runner.
 *
 * @param request - Single-company payload for the existing FactSet route.
 * @param timeoutMs - Deadline for this individual company.
 * @param signal - Cancellation signal for the whole agent run.
 * @returns The normalized single-company lookup response.
 */
export type IdLookupBatchLookup = (
  request: IdLookupRequest,
  timeoutMs: number,
  signal?: AbortSignal,
) => Promise<IdLookupResponse>;

/** Display-only aggregate emitted as companies finish within one batch. */
export interface IdLookupBatchProgress {
  completed: number;
  total: number;
  usable: number;
  failed: number;
}

/** Clock and transport controls used to test the batch runner without real time. */
export interface IdLookupBatchRuntime {
  /** Current time in milliseconds. */
  now?: () => number;
  /** Wait helper used for the shared rate-limit pause. */
  sleep?: (milliseconds: number, signal?: AbortSignal) => Promise<void>;
  /** Overrides {@link ID_LOOKUP_BATCH_CONCURRENCY} in tests. */
  concurrency?: number;
  /** Overrides {@link ID_LOOKUP_BATCH_TIMEOUT_MS} in tests. */
  batchTimeoutMs?: number;
  /** Receives best-effort progress updates without affecting lookup execution. */
  onProgress?: (progress: IdLookupBatchProgress) => void;
}

/**
 * Argument error raised before any FactSet request starts.
 *
 * The name is part of the contract: the ES5 bundle can lose the prototype, so
 * callers also classify this error by `name`.
 */
export class IdLookupBatchInputError extends Error {
  /**
   * @param message - Agent-visible explanation of the invalid argument.
   */
  constructor(message: string) {
    super(message);
    this.name = ID_LOOKUP_INPUT_ERROR_NAME;
    Object.setPrototypeOf(this, IdLookupBatchInputError.prototype);
  }
}

const FILTERS_SCHEMA = {
  type: ["object", "null"],
  description:
    "Optional public-equity listing filters. Pass null for the normal ranked " +
    "public-and-private lookup. Only provide filters when the user explicitly " +
    "requests a listing region or security type. Values within each list are " +
    "OR conditions; listing_regions and security_types together are an AND " +
    "condition. These filters apply to public equity listings and do not filter " +
    "private-company candidates.",
  properties: {
    listing_regions: {
      type: ["array", "null"],
      minItems: 1,
      description:
        "Explicit listing locations, not company domiciles. Pass null unless " +
        "the requested listing market matters. For example, united_states means " +
        "a US-listed security and united_kingdom means a UK-listed security.",
      items: {
        type: "string",
        enum: [...ID_LOOKUP_LISTING_REGION_NAMES],
      },
    },
    security_types: {
      type: ["array", "null"],
      minItems: 1,
      description:
        "Explicit public-equity security types. Pass null unless the requested " +
        `security type matters. ${describeIdLookupSecurityTypes()}.`,
      items: {
        type: "string",
        enum: [...ID_LOOKUP_SECURITY_TYPE_NAMES],
      },
    },
  },
  required: ["listing_regions", "security_types"],
  additionalProperties: false,
} as const;

/**
 * Strict tool parameters for one batched company-identity lookup.
 *
 * `uniqueItems` is intentionally absent because OpenAI strict function calling
 * does not support it. Duplicate keys are rejected by {@link parseIdLookupBatch}.
 */
export const ID_LOOKUP_BATCH_PARAMETERS = {
  type: "object",
  properties: {
    lookups: {
      type: "array",
      minItems: 1,
      description:
        "Companies to resolve. Use one entry per company and include every " +
        "company whose input is already known. Preserve the workbook row or " +
        "other logical source in request_key.",
      items: {
        type: "object",
        properties: {
          request_key: {
            type: "string",
            minLength: 1,
            description:
              "Unique correlation key for this company, such as the worksheet " +
              "row or cell that supplied it. The same key is echoed in the result.",
          },
          search_text: {
            type: "string",
            minLength: 1,
            description:
              "Exactly one company name, ticker, ISIN, CUSIP, SEDOL, or FactSet " +
              "identifier. Do not pass multiple companies, an analytical question, " +
              "or workbook instructions.",
          },
          filters: FILTERS_SCHEMA,
        },
        required: ["request_key", "search_text", "filters"],
        additionalProperties: false,
      },
    },
  },
  required: ["lookups"],
  additionalProperties: false,
} as const;

/**
 * Tool description shown to the native ReAct agent.
 */
export const ID_LOOKUP_BATCH_DESCRIPTION =
  "Resolve company names, tickers, ISINs, CUSIPs, SEDOLs, or FactSet identifiers " +
  "into authoritative FactSet company identifiers. Use this before generating " +
  "formulas or writing results whenever the correct company identity is uncertain. " +
  "Each lookup searches public and private companies and returns ranked candidates " +
  "with FactSet ID, ticker, entity ID, company type, domicile, website, and " +
  "ultimate-parent information. This is an identity-resolution tool, not a " +
  "company-research, screening, or analytical tool. " +
  "Submit one company per lookups entry and include every company whose input " +
  "is already known in that call. Normally pass filters=null. Only filter when the user " +
  "explicitly specifies a listing region or security type. Listing region means " +
  "where the security is listed, not where the company is headquartered. Never " +
  "silently choose between materially ambiguous candidates.";

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function utf8ByteLength(value: string): number {
  return new TextEncoder().encode(value).byteLength;
}

function invalid(message: string): never {
  throw new IdLookupBatchInputError(message);
}

function parseSemanticList(
  rawValue: unknown,
  field: "listing_regions" | "security_types",
  allowed: readonly string[],
  translate: (name: string) => string,
): string[] | null {
  if (rawValue == null) return null;
  if (
    !Array.isArray(rawValue) ||
    rawValue.length === 0 ||
    rawValue.some((value) => typeof value !== "string" || !value.trim())
  ) {
    invalid(
      `filters.${field} must be null or a non-empty array of supported values.`,
    );
  }
  const names = rawValue.map((value) => (value as string).trim());
  const unsupported = names.find((name) => !allowed.includes(name));
  if (unsupported) {
    invalid(`filters.${field} contains unsupported value "${unsupported}".`);
  }
  return Array.from(new Set(names.map(translate)));
}

function parseFilters(rawValue: unknown): IdLookupFilters | null {
  if (rawValue == null) return null;
  if (!isRecord(rawValue)) invalid("filters must be an object or null.");
  const unknownKey = Object.keys(rawValue).find(
    (key) => key !== "listing_regions" && key !== "security_types",
  );
  if (unknownKey) invalid(`filters.${unknownKey} is not supported.`);
  if (!Object.prototype.hasOwnProperty.call(rawValue, "listing_regions")) {
    invalid("filters.listing_regions is required. Pass null when no region filter applies.");
  }
  if (!Object.prototype.hasOwnProperty.call(rawValue, "security_types")) {
    invalid("filters.security_types is required. Pass null when no security-type filter applies.");
  }
  const regions = parseSemanticList(
    rawValue.listing_regions,
    "listing_regions",
    ID_LOOKUP_LISTING_REGION_NAMES,
    listingRegionWireValue,
  );
  const securityTypes = parseSemanticList(
    rawValue.security_types,
    "security_types",
    ID_LOOKUP_SECURITY_TYPE_NAMES,
    securityTypeWireValue,
  );
  if (!regions && !securityTypes) return null;
  return {
    include: {
      equityType: canonicalWireValues(securityTypes),
      region: canonicalWireValues(regions),
    },
    exclude: null,
  };
}

/**
 * Put equivalent filter values into one order so deduplication does not depend
 * on the order the model supplied.
 *
 * @param values - Translated FactSet wire values, or null when that filter is unused.
 * @returns A sorted copy, or null when there is nothing to filter.
 */
function canonicalWireValues(values: string[] | null): string[] | null {
  if (!values || values.length === 0) return null;
  return [...values].sort();
}

/**
 * Validate a model-facing batch and translate it to single-company requests.
 *
 * The whole batch is rejected before any entry can reach FactSet.
 *
 * @param rawInput - Untrusted tool arguments.
 * @returns Validated entries in the submitted order.
 * @throws {IdLookupBatchInputError} When the batch shape, keys, or filters are invalid.
 */
export function parseIdLookupBatch(rawInput: unknown): ParsedIdLookupEntry[] {
  if (!isRecord(rawInput)) invalid("id_lookup requires an object input.");
  const unknownKey = Object.keys(rawInput).find((key) => key !== "lookups");
  if (unknownKey) invalid(`Unknown id_lookup field: ${unknownKey}.`);
  if (!Array.isArray(rawInput.lookups)) {
    invalid("id_lookup requires a lookups array.");
  }
  if (rawInput.lookups.length < 1) {
    invalid("lookups must contain at least one entry.");
  }

  const seenKeys = new Set<string>();
  return rawInput.lookups.map((rawEntry, index) => {
    if (!isRecord(rawEntry)) {
      invalid(`lookups[${index}] must be an object.`);
    }
    const unknownEntryKey = Object.keys(rawEntry).find(
      (key) => key !== "request_key" && key !== "search_text" && key !== "filters",
    );
    if (unknownEntryKey) {
      invalid(`lookups[${index}].${unknownEntryKey} is not supported.`);
    }
    const requestKey =
      typeof rawEntry.request_key === "string" ? rawEntry.request_key.trim() : "";
    if (!requestKey) invalid(`lookups[${index}].request_key is required.`);
    if (seenKeys.has(requestKey)) {
      invalid(`Duplicate request_key "${requestKey}". Each lookup entry needs its own key.`);
    }
    seenKeys.add(requestKey);
    const searchText =
      typeof rawEntry.search_text === "string" ? rawEntry.search_text.trim() : "";
    if (!searchText) invalid(`lookups[${index}].search_text is required.`);
    if (utf8ByteLength(searchText) > 512) {
      invalid(`lookups[${index}].search_text exceeds the 512-byte limit.`);
    }
    if (!Object.prototype.hasOwnProperty.call(rawEntry, "filters")) {
      invalid(`lookups[${index}].filters is required. Pass null for a normal lookup.`);
    }
    return {
      requestKey,
      searchText,
      request: {
        search_text: searchText,
        filters: parseFilters(rawEntry.filters),
        limit: FACTSET_ID_LOOKUP_DEFAULT_LIMIT,
      },
    };
  });
}

function dedupeKey(entry: ParsedIdLookupEntry): string {
  return JSON.stringify(entry.request);
}

function failureResult(
  entry: ParsedIdLookupEntry,
  error: string,
  errorCode: string | null,
  reauthorizationUrl?: string,
): Record<string, unknown> {
  return {
    request_key: entry.requestKey,
    search_text: entry.searchText,
    success: false,
    status: "error",
    candidate_count: 0,
    candidates: [],
    parent_enrichment_status: "unavailable",
    fallback_recommended: true,
    fallback_reason: errorCode ?? "id_lookup_failed",
    error_code: errorCode,
    error,
    ...(reauthorizationUrl ? { reauthorization_url: reauthorizationUrl } : {}),
  };
}

function successResult(
  entry: ParsedIdLookupEntry,
  response: IdLookupResponse,
): Record<string, unknown> {
  const { meta: _meta, datasets: _datasets, ...modelResult } = response;
  return {
    request_key: entry.requestKey,
    ...modelResult,
    search_text: entry.searchText,
  };
}

function classifyLookupError(
  entry: ParsedIdLookupEntry,
  error: unknown,
): Record<string, unknown> {
  if (error instanceof IdLookupTimeoutError) {
    return failureResult(
      entry,
      TIMEOUT_MESSAGE,
      FACTSET_ID_LOOKUP_ERROR_CODES.TIMEOUT,
    );
  }
  if (error instanceof IdLookupReauthRequiredError) {
    return failureResult(
      entry,
      error.message,
      error.serverErrorCode,
      error.reauthorizationUrl,
    );
  }
  if (error instanceof IdLookupFailedError) {
    const code = error.serverErrorCode ?? FACTSET_ID_LOOKUP_ERROR_CODES.UPSTREAM_ERROR;
    const upstreamDetail =
      typeof error.details === "string" && error.details.trim()
        ? error.details.trim()
        : "";
    const message =
      code === FACTSET_ID_LOOKUP_ERROR_CODES.RATE_LIMITED
        ? upstreamDetail && upstreamDetail !== RATE_LIMIT_MESSAGE
          ? `${RATE_LIMIT_MESSAGE} ${upstreamDetail}`
          : RATE_LIMIT_MESSAGE
        : error.message;
    return failureResult(entry, message, code);
  }
  return failureResult(
    entry,
    error instanceof Error ? error.message : String(error),
    null,
  );
}

async function defaultSleep(
  milliseconds: number,
  signal?: AbortSignal,
): Promise<void> {
  if (milliseconds <= 0) return;
  await new Promise<void>((resolve, reject) => {
    const timer = setTimeout(() => {
      signal?.removeEventListener("abort", onAbort);
      resolve();
    }, milliseconds);
    const onAbort = () => {
      clearTimeout(timer);
      reject(abortError("Company lookup cancelled."));
    };
    if (signal?.aborted) {
      onAbort();
      return;
    }
    signal?.addEventListener("abort", onAbort, { once: true });
  });
}

function abortError(message: string): Error {
  const error = new Error(message);
  error.name = "AbortError";
  return error;
}

/**
 * Run validated lookups with bounded concurrency and isolated failures.
 *
 * Identical company-and-filter requests share one upstream call. Every input
 * entry still receives its own result, in input order. A timeout, rate limit,
 * or no-match on one entry does not discard the others.
 *
 * @param entries - Output of {@link parseIdLookupBatch}.
 * @param lookup - Single-company transport. Production uses the existing FactSet route.
 * @param signal - Optional cancellation signal for the agent run.
 * @param runtime - Optional clock and concurrency controls.
 * @returns Model-facing results aligned with `entries`.
 * @throws {Error} When `signal` aborts the run. Individual lookup failures are returned.
 */
export async function runIdLookupBatch(
  entries: readonly ParsedIdLookupEntry[],
  lookup: IdLookupBatchLookup,
  signal?: AbortSignal,
  runtime: IdLookupBatchRuntime = {},
): Promise<Array<Record<string, unknown>>> {
  const now = runtime.now ?? Date.now;
  const sleep = runtime.sleep ?? defaultSleep;
  const concurrency = runtime.concurrency ?? ID_LOOKUP_BATCH_CONCURRENCY;
  const deadline = now() + (runtime.batchTimeoutMs ?? ID_LOOKUP_BATCH_TIMEOUT_MS);
  const results = new Array<Record<string, unknown>>(entries.length);
  const groups = new Map<string, number[]>();
  entries.forEach((entry, index) => {
    const key = dedupeKey(entry);
    const indexes = groups.get(key);
    if (indexes) indexes.push(index);
    else groups.set(key, [index]);
  });
  const work = Array.from(groups.values());
  let rateLimitCooldown: Promise<void> | null = null;
  const getRateLimitCooldown = (): Promise<void> => {
    if (rateLimitCooldown) return rateLimitCooldown;
    const cooldown = sleep(
      FACTSET_ID_LOOKUP_MAX_RETRY_DELAY_MS,
      signal,
    ).finally(() => {
      if (rateLimitCooldown === cooldown) rateLimitCooldown = null;
    });
    rateLimitCooldown = cooldown;
    return cooldown;
  };
  let cursor = 0;
  let completed = 0;
  let usable = 0;
  let failed = 0;
  const emitProgress = (): void => {
    try {
      runtime.onProgress?.({
        completed,
        total: entries.length,
        usable,
        failed,
      });
    } catch {
      // Display-only progress must never change lookup execution.
    }
  };
  emitProgress();

  const worker = async (): Promise<void> => {
    while (cursor < work.length) {
      if (signal?.aborted) throw abortError("Company lookup cancelled.");
      const indexes = work[cursor];
      cursor += 1;
      if (!indexes) return;
      if (rateLimitCooldown) await rateLimitCooldown;
      if (signal?.aborted) throw abortError("Company lookup cancelled.");
      const representative = entries[indexes[0]];
      const remainingMs = deadline - now();
      let result: Record<string, unknown>;
      if (remainingMs <= 0) {
        result = failureResult(
          representative,
          TIMEOUT_MESSAGE,
          FACTSET_ID_LOOKUP_ERROR_CODES.TIMEOUT,
        );
      } else {
        try {
          const response = await lookup(
            representative.request,
            Math.min(FACTSET_ID_LOOKUP_CLIENT_TIMEOUT_MS, remainingMs),
            signal,
          );
          result = successResult(representative, response);
        } catch (error) {
          if (signal?.aborted) throw error;
          result = classifyLookupError(representative, error);
          if (result.error_code === FACTSET_ID_LOOKUP_ERROR_CODES.RATE_LIMITED) {
            await getRateLimitCooldown();
          }
        }
      }
      for (const index of indexes) {
        const entry = entries[index];
        results[index] = {
          ...result,
          request_key: entry.requestKey,
          search_text: entry.searchText,
        };
      }
      const matched =
        result.success === true && Number(result.candidate_count ?? 0) > 0;
      completed += indexes.length;
      if (matched) usable += indexes.length;
      else failed += indexes.length;
      emitProgress();
    }
  };

  await Promise.all(
    Array.from({ length: Math.min(concurrency, work.length) }, () => worker()),
  );
  return results;
}
